import { useEffect, useState } from 'react'
import { FeedPageParams, IBranding } from '@/feed/Feed.interface'
import { retryBranding } from '@/lib/utils'

/**
 * Fetches branding for the current app. Callers should hold off rendering the
 * toolbar until isLoading is false, otherwise the unbranded defaults flash
 * before the branded values arrive.
 */
export default function useBranding(
  pageParams: FeedPageParams,
  maxRetries: number = 1,
  enabled: boolean = true
) {
  const [branding, setBranding] = useState<IBranding | null>(null)
  const [isLoading, setIsLoading] = useState(enabled)
  const { alias, sdkAppId, sdkAppKey } = pageParams

  useEffect(() => {
    if (!enabled) {
      setBranding(null)
      setIsLoading(false)
      return undefined
    }

    let cancelled = false
    setIsLoading(true)

    retryBranding(
      {
        params: { alias },
        key: `${sdkAppId}${sdkAppKey}`,
        additionalHeaders: { source: 'native' }
      },
      maxRetries
    )
      .then((result) => {
        if (!cancelled) setBranding(result)
      })
      .catch(() => {
        if (!cancelled) setBranding(null)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [alias, sdkAppId, sdkAppKey, maxRetries, enabled])

  return { branding, isLoading }
}
